/**
 * Page-tracking path filter. Public token pages carry a CREDENTIAL in the path — the signed
 * token is the only thing guarding them. usePageTracking() runs in App.tsx before the pre-auth
 * early returns, so a phone that also holds a staff JWT would POST that token to
 * /api/activity/track (and into activity logs) unless it is stripped here.
 *
 *  - /van/day/<token> (driver day page) → never tracked (null).
 *  - /v/s/<token>, /v/c/<token>, /v/r/<ref> → token segment replaced with ':token'.
 */
const NEVER_TRACK = [/^\/van\/day(\/|$)/i];
const REDACT = /^\/v\/(s|c|r)\/[^/?#]+/i;

export function trackablePath(path: string): string | null {
  const p = String(path ?? '');
  if (NEVER_TRACK.some((re) => re.test(p))) return null;
  return p.replace(REDACT, (_m, kind: string) => `/v/${kind}/:token`);
}

/**
 * Token-redacting pass for any string that may hold a URL (referrer, Sentry fields):
 *  - /v/s|c|r/<token>            → /v/<kind>/:token
 *  - /van/day/<token>[/…]         → /van/day/:token[/…]
 *  - /api/van/day/<token>[/…]     → /api/van/day/:token[/…]
 *  - the same paths percent-encoded (%2Fvan%2Fday%2F<token>, %2Fv%2Fs%2F<token>, …)
 * Idempotent. Everything else untouched. (Page tracking still skips /van/day/ entirely —
 * see trackablePath above.)
 */
export function redactUrl(url: string): string {
  const u = String(url ?? '');
  return u
    .replace(/\/v\/(s|c|r)\/[^/?#\s"']+/gi, (_m, kind: string) => `/v/${kind}/:token`)
    .replace(/\/van\/day\/[^/?#\s"']+/gi, '/van/day/:token')
    // Percent-encoded forms (e.g. a URL inside a query string: ?next=%2Fvan%2Fday%2F<token>).
    .replace(/%2Fv%2F(s|c|r)%2F[^%/?#&\s"']+/gi, (_m, kind: string) => `%2Fv%2F${kind}%2F:token`)
    .replace(/%2Fvan%2Fday%2F[^%/?#&\s"']+/gi, '%2Fvan%2Fday%2F:token');
}
